import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import * as D from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import "./platform.css";

/** Eigene Bausteine der Plattform-Verwaltung (Maestro-Stil) — unabhängig vom CRM-Design. */

export const C = {
  green: "#34d399",
  amber: "#fbbf24",
  red: "#fb7185",
  blue: "#60a5fa",
  violet: "#a78bfa",
  cyan: "#22d3ee",
  gray: "#9aa0b8",
};

export const cx = (...p: (string | false | null | undefined)[]) => p.filter(Boolean).join(" ");

export function Btn({
  variant = "default",
  size,
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "danger" | "ghost";
  size?: "sm";
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={cx("pf-btn", variant !== "default" && variant, size, className)}
    >
      {loading && <span className="pf-spin" />}
      {children}
    </button>
  );
}

export function Pill({ color = C.gray, children, dot }: { color?: string; children: ReactNode; dot?: boolean }) {
  return (
    <span className="pf-pill" style={{ ["--c" as string]: color }}>
      {dot && <span className="pf-dot" style={{ ["--c" as string]: color, width: 6, height: 6 }} />}
      {children}
    </span>
  );
}

export function Dot({ color = C.green, off }: { color?: string; off?: boolean }) {
  return <span className={cx("pf-dot", off && "off")} style={{ ["--c" as string]: color }} />;
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label?: string }>(
  function Input({ label, className, ...p }, ref) {
    return (
      <div className="pf-field">
        {label && <label>{label}</label>}
        <input ref={ref} {...p} className={cx("pf-input", className)} />
      </div>
    );
  },
);

export function Select({
  label,
  options,
  className,
  ...p
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; options: { value: string; label: string }[] }) {
  return (
    <div className="pf-field">
      {label && <label>{label}</label>}
      <select {...p} className={cx("pf-select", className)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Textarea({ label, className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string }) {
  return (
    <div className="pf-field">
      {label && <label>{label}</label>}
      <textarea {...p} className={cx("pf-textarea", className)} />
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="pf-switch"
      onClick={() => onChange(!checked)}
    />
  );
}

/** Logo des Kunden auf heller Platte (Kunden-Logos sind für helle Flächen gemacht); ohne Logo: Initialen. */
export function LogoPlate({ url, name, size = 56, wide }: { url?: string | null; name: string; size?: number; wide?: boolean }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  const initials =
    name
      .replace(/–.*$/, "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?";
  if (url && !failed) {
    return (
      <div className={cx("pf-logo", wide && "wide")} style={{ ["--s" as string]: `${size}px` }}>
        <img src={url} alt={name} onError={() => setFailed(true)} />
      </div>
    );
  }
  return (
    <div className="pf-logo is-initials" style={{ ["--s" as string]: `${size}px` }}>
      {initials}
    </div>
  );
}

/** Zahl, die beim Erscheinen hochzählt. */
export function CountUp({ value, className }: { value: number; className?: string }) {
  const [v, setV] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setV(value);
      return;
    }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / 900);
      const e = 1 - Math.pow(1 - p, 3);
      setV(Math.round(a + (value - a) * e));
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span className={className}>{v.toLocaleString("de-AT")}</span>;
}

export function Ring({ pct, tone, label }: { pct: number; tone?: "warn" | "bad"; label: string }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  return (
    <div className="pf-ring">
      <svg width="92" height="92" viewBox="0 0 92 92">
        <defs>
          <linearGradient id="pf-ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#c026d3" />
          </linearGradient>
        </defs>
        <circle className="track" cx="46" cy="46" r={r} fill="none" strokeWidth="7" />
        <circle
          className={cx("val", tone)}
          cx="46"
          cy="46"
          r={r}
          fill="none"
          strokeWidth="7"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.min(100, Math.max(0, pct)) / 100)}
        />
      </svg>
      <div className="pf-ring-label">{label}</div>
    </div>
  );
}

/** Dialog im Plattform-Stil. Die Klasse "pf" kommt an jedes Portal-Element, weil sie sonst nicht greift. */
export function Modal({
  open,
  onClose,
  title,
  lead,
  logo,
  width = 560,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  lead?: ReactNode;
  logo?: ReactNode;
  width?: number;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <D.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <D.Portal>
        <D.Overlay className="pf pf-overlay" />
        <D.Content className="pf pf-modal" style={{ ["--w" as string]: `${width}px` }} aria-describedby={undefined}>
          <div className="pf-modal-head">
            {logo}
            <div style={{ minWidth: 0 }}>
              <D.Title className="pf-modal-title">{title}</D.Title>
              {lead && <div className="pf-lead">{lead}</div>}
            </div>
            <D.Close asChild>
              <Btn variant="ghost" size="sm" className="pf-close" aria-label="Schließen">
                <X size={16} />
              </Btn>
            </D.Close>
          </div>
          {children}
          {footer && <div className="pf-modal-foot">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function Confirm({
  open,
  title,
  message,
  confirmLabel,
  danger,
  loading,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={460}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Abbrechen
          </Btn>
          <Btn variant={danger ? "danger" : "primary"} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Btn>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--pf-text-2)" }}>{message}</p>
    </Modal>
  );
}

/* ───────── Hinweise (Toasts) im Plattform-Stil ───────── */
import { createContext, useCallback, useContext } from "react";

type Toast = { id: number; kind: "ok" | "err"; title: string; text?: string };
const ToastCtx = createContext<(t: Omit<Toast, "id">) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, "id">) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { ...t, id }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), t.kind === "err" ? 7000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div
        className="pf"
        style={{ position: "fixed", right: 18, bottom: 18, zIndex: 80, display: "flex", flexDirection: "column", gap: 10, maxWidth: 360 }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="pf-card"
            style={{
              padding: "12px 16px",
              borderColor: t.kind === "err" ? "rgba(251,113,133,.55)" : "rgba(52,211,153,.45)",
              background: "rgba(14,15,24,.94)",
            }}
          >
            <b style={{ color: t.kind === "err" ? C.red : C.green }}>{t.title}</b>
            {t.text && <div style={{ color: "var(--pf-text-2)", marginTop: 2 }}>{t.text}</div>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
